/* ═══════════════════════════════════════════════════════════════════
   VISION MUSIC — continuous, uplifting, bliss-aesthetic background loop.

   A four-chord progression (Imaj7 · V · vi · IVadd9) in C major, layered:
     • Sub-bass root drone (sine, one octave down)
     • Warm choir-pad (three sine voices per chord + soft 2nd harmonic)
     • Sparse pentatonic melody accents (crystal chime bloom)
     • Slow high-shimmer LFO'd sine
     • Very gentle "breath" pulse (soft, felt, not heard)

   Zero-storage, zero-cost, offline-first.  Runs forever until stop().
   Global lowpass keeps the whole mix warm — never bright or arcade-y.

   API:
     start()   — begin/resume the loop
     stop()    — stop everything and release resources
     isPlaying()
     setMuted(bool) / isMuted()
   ═══════════════════════════════════════════════════════════════════ */

import { getAudioCtx } from './audioCore';

const MUTE_KEY = 'vision_music_muted';

let ctx = null;
let master = null;
let masterLP = null;
let loopHandle = null;         // setTimeout handle for the next chord
let running = false;
let nodes = [];                // active oscillators/gains so we can dispose

const readMuted = () => {
  try { return window.localStorage.getItem(MUTE_KEY) === '1'; }
  catch (_) { return false; }
};

export const setMuted = (m) => {
  try { window.localStorage.setItem(MUTE_KEY, m ? '1' : '0'); }
  catch (_) { /* noop */ }
  if (m) stop();
};

export const isMuted = readMuted;
export const isPlaying = () => running;

const ensureCtx = () => {
  if (readMuted()) return null;
  if (!ctx || !master) {
    try {
      ctx = getAudioCtx();
      if (!ctx) return null;
      master = ctx.createGain();
      master.gain.value = 0.0;   // ramped up on start
      // Warm global low-pass (never bright / arcade-y) + high-pass to strip
      // sub-rumble that was making chord changes sound muddy ("cross-talk").
      masterLP = ctx.createBiquadFilter();
      masterLP.type = 'lowpass';
      masterLP.frequency.value = 3800;
      masterLP.Q.value = 0.55;
      const masterHP = ctx.createBiquadFilter();
      masterHP.type = 'highpass';
      masterHP.frequency.value = 90;
      masterHP.Q.value = 0.6;
      master.connect(masterHP);
      masterHP.connect(masterLP);
      masterLP.connect(ctx.destination);
    } catch (_) { return null; }
  }
  if (ctx.state === 'suspended') {
    try { ctx.resume(); } catch (_) { /* noop */ }
  }
  return ctx;
};

/* Attach oscillator to `nodes` so stop() can dispose everything. */
const track = (osc, gain) => {
  nodes.push({ osc, gain });
};

/* Slow bloom envelope on gain node. */
const bloomEnv = (g, peak, t0, a, h, r) => {
  const gv = g.gain;
  gv.cancelScheduledValues(t0);
  gv.setValueAtTime(0.0001, t0);
  gv.exponentialRampToValueAtTime(peak, t0 + a);
  gv.setValueAtTime(peak, t0 + a + h);
  gv.exponentialRampToValueAtTime(0.0001, t0 + a + h + r);
};

/* Sustained sine voice with soft 2nd-harmonic sparkle.  Release is short
   (0.35 s) so consecutive chords hand off CLEANLY without their tails
   overlapping — this is what was causing the muddy "cross-talk". */
const chordVoice = (freq, peak, t0, dur) => {
  const RELEASE = 0.35;
  const HOLD    = Math.max(0.1, dur - 0.5 - RELEASE);   // leave 0.5s of attack + air
  const ATTACK  = 0.5;
  const fund = ctx.createOscillator();
  const g = ctx.createGain();
  fund.type = 'sine';
  fund.frequency.value = freq;
  bloomEnv(g, peak, t0, ATTACK, HOLD, RELEASE);
  fund.connect(g); g.connect(master);
  fund.start(t0);
  fund.stop(t0 + ATTACK + HOLD + RELEASE + 0.05);
  track(fund, g);
  // Very soft octave sparkle — only on the upper voices to avoid extra bass build-up.
  if (freq >= 300) {
    const h2 = ctx.createOscillator();
    const hg = ctx.createGain();
    h2.type = 'sine';
    h2.frequency.value = freq * 2;
    bloomEnv(hg, peak * 0.10, t0, ATTACK + 0.1, HOLD, RELEASE);
    h2.connect(hg); hg.connect(master);
    h2.start(t0);
    h2.stop(t0 + ATTACK + HOLD + RELEASE + 0.1);
    track(h2, hg);
  }
};

/* Sub-bass root drone.  Same short release as chord voices — no overlap. */
const bassNote = (freq, t0, dur) => {
  const RELEASE = 0.35;
  const HOLD    = Math.max(0.1, dur - 0.5 - RELEASE);
  const ATTACK  = 0.5;
  const b = ctx.createOscillator();
  const g = ctx.createGain();
  b.type = 'sine';
  b.frequency.value = freq / 2;
  bloomEnv(g, 0.22, t0, ATTACK, HOLD, RELEASE);
  b.connect(g); g.connect(master);
  b.start(t0);
  b.stop(t0 + ATTACK + HOLD + RELEASE + 0.05);
  track(b, g);
};

/* Sparse melodic pentatonic bloom during a chord. Very quiet — pure sparkle. */
const melodyBloom = (freq, t0) => {
  const m = ctx.createOscillator();
  const g = ctx.createGain();
  m.type = 'sine';
  m.frequency.value = freq;
  bloomEnv(g, 0.07, t0, 0.05, 0.12, 0.55);
  m.connect(g); g.connect(master);
  m.start(t0);
  m.stop(t0 + 0.9);
  track(m, g);
};

/* One long high-shimmer drone that LFOs slowly through the whole loop.
   Very subtle — sits behind the pad, never fights it. */
const startShimmer = (t0, totalDur) => {
  const s = ctx.createOscillator();
  const g = ctx.createGain();
  s.type = 'sine';
  s.frequency.value = 1567.98;  // G6
  const lfo = ctx.createOscillator();
  const lfoG = ctx.createGain();
  lfo.type = 'sine';
  lfo.frequency.value = 0.18;
  lfoG.gain.value = 0.012;
  lfo.connect(lfoG); lfoG.connect(g.gain);
  bloomEnv(g, 0.028, t0, 2.4, totalDur - 5.0, 2.4);
  s.connect(g); g.connect(master);
  s.start(t0); lfo.start(t0);
  s.stop(t0 + totalDur); lfo.stop(t0 + totalDur);
  track(s, g);
  track(lfo, lfoG);
};

/* Chord progression — I·V·vi·IV in C major, 4s each = 16s loop.
   Each chord uses THREE sine voices (root removed from voicing — the bass
   handles it) so the mix stays clean and voices don't stack too densely. */
const PROGRESSION = [
  // Cmaj9   root=C4
  { root: 261.63, voices: [329.63, 392.00, 587.33] },   // E4 G4 D5
  // G      root=G3
  { root: 196.00, voices: [293.66, 392.00, 493.88] },   // B3 D4 B4
  // Am     root=A3
  { root: 220.00, voices: [329.63, 440.00, 523.25] },   // E4 A4 C5
  // Fadd9  root=F3  (bright IV)
  { root: 174.61, voices: [261.63, 349.23, 440.00] },   // C4 F4 A4
];

/* Pentatonic melody notes (C major pentatonic) used to sprinkle sparkles. */
const PENTATONIC = [523.25, 587.33, 659.25, 783.99, 880.00, 1046.5]; // C5 D5 E5 G5 A5 C6

const CHORD_DUR = 4.0;                        // seconds per chord
const LOOP_DUR  = CHORD_DUR * PROGRESSION.length; // 16 s
const PEAK_MASTER = 0.24;                     // slightly lower for clarity
const FADE_IN     = 1.6;

const scheduleLoop = (t0) => {
  // Fade the master up on the very first schedule call.
  const mg = master.gain;
  if (mg.value < PEAK_MASTER - 0.001) {
    mg.cancelScheduledValues(t0);
    mg.setValueAtTime(mg.value, t0);
    mg.linearRampToValueAtTime(PEAK_MASTER, t0 + FADE_IN);
  }

  // Long high shimmer riding across the whole loop
  startShimmer(t0, LOOP_DUR);

  // For each chord: bass + 3 voices + 1 melody bloom (single, not two).
  PROGRESSION.forEach((chord, i) => {
    const ct = t0 + i * CHORD_DUR;
    bassNote(chord.root, ct, CHORD_DUR);
    chord.voices.forEach((f, vi) => {
      const peak = vi === 0 ? 0.11 : vi === 1 ? 0.10 : 0.08;
      chordVoice(f, peak, ct + vi * 0.06, CHORD_DUR - 0.1);
    });
    // ONE melody bloom per chord — mid-chord, so it never clashes with the
    // attack of the next chord.
    const mNote = PENTATONIC[Math.floor(Math.random() * PENTATONIC.length)];
    melodyBloom(mNote, ct + 1.8);
  });

  // Schedule the next loop iteration exactly LOOP_DUR later.
  const nextT = t0 + LOOP_DUR;
  const msUntilNext = Math.max(20, (nextT - ctx.currentTime - 0.15) * 1000);
  loopHandle = setTimeout(() => {
    if (!running) return;
    scheduleLoop(nextT);
    // Housekeeping: drop refs to already-stopped nodes so array doesn't leak.
    nodes = nodes.filter((n) => {
      try { return n.osc && (n.osc.playbackState !== undefined ? n.osc.playbackState < 3 : true); }
      catch (_) { return false; }
    });
  }, msUntilNext);
};

export const start = () => {
  if (running) return true;
  const c = ensureCtx();
  if (!c) return false;
  const beginScheduling = () => {
    if (running) return;
    running = true;
    scheduleLoop(c.currentTime + 0.05);
  };
  if (c.state === 'suspended') {
    try {
      const p = c.resume();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          if (c.state === 'running') beginScheduling();
        }).catch(() => { /* stays silent; caller can retry via forceRestart() */ });
        return true;
      }
    } catch (_) { /* fall through */ }
  }
  beginScheduling();
  return true;
};

/* GUARANTEED start — synchronously destroys any stuck/suspended AudioContext
   and creates a brand-new one inside the CURRENT call stack.  Call this from
   inside a click / touch handler and audio is guaranteed to play, because the
   new AudioContext is created within a user-gesture event.  Also plays a
   short "welcome chime" so the user immediately knows audio is live. */
export const forceRestart = () => {
  // Synchronous full teardown (NO setTimeout — must stay inside the gesture).
  running = false;
  if (loopHandle) { clearTimeout(loopHandle); loopHandle = null; }
  nodes.forEach((n) => {
    try { n.osc.stop(); } catch (_) { /* noop */ }
    try { n.osc.disconnect(); } catch (_) { /* noop */ }
    try { n.gain.disconnect(); } catch (_) { /* noop */ }
  });
  nodes = [];
  if (master) { try { master.disconnect(); } catch (_) { /* noop */ } }
  ctx = null; master = null; masterLP = null;

  // Fresh context — created INSIDE the calling gesture, so it will be "running".
  const c = ensureCtx();
  if (!c) return false;
  // Kick a short welcome bloom immediately (proof of life) — G5 crystal ping.
  try {
    const t0 = c.currentTime;
    const osc = c.createOscillator();
    const g   = c.createGain();
    osc.type = 'sine'; osc.frequency.value = 783.99;
    const gv = g.gain;
    gv.setValueAtTime(0.0001, t0);
    gv.exponentialRampToValueAtTime(0.14, t0 + 0.03);
    gv.exponentialRampToValueAtTime(0.0001, t0 + 0.75);
    osc.connect(g); g.connect(master);
    osc.start(t0); osc.stop(t0 + 0.9);
  } catch (_) { /* noop */ }
  // Begin the endless bliss loop right after the welcome chime.
  running = true;
  scheduleLoop(c.currentTime + 0.35);
  return true;
};

export const stop = () => {
  running = false;
  if (loopHandle) { clearTimeout(loopHandle); loopHandle = null; }
  // Fade master down quickly so we don't click, then hard-stop nodes.
  if (ctx && master) {
    const t = ctx.currentTime;
    try {
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(0.0001, t + 0.35);
    } catch (_) { /* noop */ }
  }
  // Actually stop oscillators after the fade.
  const fadingMaster = master;
  const fadingNodes = nodes;
  nodes = [];
  master = null; masterLP = null;
  setTimeout(() => {
    fadingNodes.forEach((n) => {
      try { n.osc.stop(); } catch (_) { /* noop */ }
      try { n.osc.disconnect(); } catch (_) { /* noop */ }
      try { n.gain.disconnect(); } catch (_) { /* noop */ }
    });
    if (fadingMaster) { try { fadingMaster.disconnect(); } catch (_) { /* noop */ } }
  }, 420);
};
